import React, {
	useCallback,
	useRef,
	useState,
	useEffect,
	ComponentType,
	Ref,
	forwardRef,
} from 'react';
import apiClient from '../../../Services/ApiClient';
import TextField from '../TextField/TextField';
import { joinClasses } from '../../../Utils/className.util';
import styles from './AddressWhisperer.module.scss';
import AddressSuggestionsDropdown from './AddressSuggestionsDropdown';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;

export interface AddressConfig {
	apiEndpoint?: string;
	queryParamName?: string;
	fieldMapping?: Record<string, string>;
	onAddressSelect?: (item: Record<string, unknown>) => void;
}

interface AddressWhispererProps {
	addressConfig?: AddressConfig | null;
	forwardRef?: Ref<HTMLInputElement>;
	[key: string]: unknown;
}

function AddressWhisperer({
	addressConfig,
	forwardRef,
	...textFieldProps
}: AddressWhispererProps) {
	const [suggestions, setSuggestions] = useState<Record<string, unknown>[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showDropdown, setShowDropdown] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const suppressSearchRef = useRef(false);

	const hasAddressConfig = addressConfig && typeof addressConfig === 'object';
	const apiEndpoint = addressConfig?.apiEndpoint;
	const queryParam = addressConfig?.queryParamName;

	const searchAddresses = useCallback(
		async (searchText: string) => {
			if (
				!apiEndpoint ||
				!queryParam ||
				!searchText ||
				searchText.length < MIN_SEARCH_LENGTH
			) {
				setSuggestions([]);
				return;
			}

			setIsLoading(true);
			setShowDropdown(true);

			try {
				const url = `${apiEndpoint}?${queryParam}=${encodeURIComponent(
					searchText,
				)}`;
				const res = await apiClient.get(url, { catchError: false });

				if (res?.data?.error || (res?.status ?? 0) >= 400) {
					setSuggestions([]);
				} else if (res?.data) {
					const data = Array.isArray(res.data)
						? res.data
						: res.data?.data ?? [];
					setSuggestions(data);
				} else {
					setSuggestions([]);
				}
			} catch {
				setSuggestions([]);
			} finally {
				setIsLoading(false);
			}
		},
		[apiEndpoint, queryParam],
	);

	const handleChange = useCallback(
		(value: string) => {
			(textFieldProps.onChange as ((value: string) => void) | undefined)?.(
				value,
			);

			if (!hasAddressConfig) return;
			if (suppressSearchRef.current) return;

			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}

			if (!value || value.length < MIN_SEARCH_LENGTH) {
				setSuggestions([]);
				setShowDropdown(false);
				return;
			}

			debounceRef.current = setTimeout(() => {
				searchAddresses(value);
			}, DEBOUNCE_MS);
		},
		[hasAddressConfig, searchAddresses, textFieldProps.onChange],
	);

	const handleSuggestionSelect = useCallback(
		(item: Record<string, unknown>) => {
			suppressSearchRef.current = true;
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = null;
			addressConfig?.onAddressSelect?.(item);
			setSuggestions([]);
			setShowDropdown(false);
			containerRef.current?.querySelector<HTMLInputElement>('input')?.blur();
			requestAnimationFrame(() => {
				suppressSearchRef.current = false;
			});
		},
		[addressConfig?.onAddressSelect],
	);

	const handleClickOutside = useCallback((e: MouseEvent) => {
		if (!containerRef.current?.contains(e.target as Node)) {
			setShowDropdown(false);
		}
	}, []);

	useEffect(() => {
		if (hasAddressConfig) {
			document.addEventListener('click', handleClickOutside, true);
		}
		return () => {
			document.removeEventListener('click', handleClickOutside, true);
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [hasAddressConfig, handleClickOutside]);

	const TextFieldComponent = TextField as ComponentType<
		Record<string, unknown>
	>;

	// Without addressConfig – transparent pass-through to standard TextField
	if (!hasAddressConfig) {
		return <TextFieldComponent {...textFieldProps} ref={forwardRef} />;
	}

	return (
		<div
			ref={containerRef}
			className={joinClasses([
				textFieldProps.wrapperClassName,
				styles.container,
			])}>
			<TextFieldComponent
				{...textFieldProps}
				ref={forwardRef}
				onChange={handleChange}
				bounce={0}
			/>
			{showDropdown && (
				<div className={styles.dropdown}>
					<AddressSuggestionsDropdown
						isLoading={isLoading}
						suggestions={suggestions}
						onSelect={handleSuggestionSelect}
					/>
				</div>
			)}
		</div>
	);
}

export default forwardRef<
	HTMLInputElement,
	Omit<AddressWhispererProps, 'forwardRef'>
>((props, ref) => <AddressWhisperer {...props} forwardRef={ref} />);
